<script setup lang="ts">
import { computed, shallowRef } from 'vue';
import { ApiError } from '@/utils/errors';
import { i18nServerMessage } from '@/utils/helpers/rest-response';
import { OmsOrder } from '@/modules/marketplace/models';
import { useMarketplaceApiService } from '@/modules/marketplace/services/marketplace-api.service';
import { store as vuex } from '@/store/store';

const marketplaceApiService = useMarketplaceApiService();

const props = withDefaults(
  defineProps<{ orderRef: string; status: OmsOrder['status']; icon?: boolean }>(),
  { icon: false }
);

const emit = defineEmits<{
  (e: 'action', payload: { error?: boolean; orderRef: string; message: string });
}>();

const hasTraderUserRole = computed(() => vuex.getters.hasTraderUserRole);

const isLoading = shallowRef(false);

async function toggle(): Promise<void> {
  try {
    isLoading.value = true;

    if (props.status === 'HELD') {
      await route();
    } else {
      await unroute();
    }
  } catch (err) {
    emit('action', {
      error: true,
      orderRef: props.orderRef,
      message: `Operation failed: ${new ApiError(i18nServerMessage(err as Error)).message}`,
    });
  } finally {
    isLoading.value = false;
  }
}

async function unroute(): Promise<void> {
  await marketplaceApiService.unrouteOrder(props.orderRef);

  emit('action', {
    orderRef: props.orderRef,
    message: 'Order has been successfully deactivated',
  });
}

async function route(): Promise<void> {
  await marketplaceApiService.routeOrder(props.orderRef);

  emit('action', {
    orderRef: props.orderRef,
    message: 'Order has been successfully activated',
  });
}
</script>

<template>
  <span>
    <!-- icon button variant -->
    <AuroraBtn
      v-if="icon"
      class="icon-action"
      data-test="toggle-order-icon"
      :disabled="isLoading || !hasTraderUserRole"
      icon
      :loading="isLoading"
      timeframe="createLoans"
      x-small
      @click="toggle()"
    >
      <VIcon size="24">{{ status === 'HELD' ? 'mdi-share' : 'mdi-reply' }}</VIcon>
    </AuroraBtn>

    <!-- regular button variant -->
    <AuroraBtn
      v-else
      color="purple"
      data-test="toggle-order-btn"
      :disabled="isLoading || !hasTraderUserRole"
      :loading="isLoading"
      timeframe="createLoans"
      @click="toggle()"
    >
      {{ status === 'HELD' ? 'Activate' : 'Deactivate' }}
    </AuroraBtn>
  </span>
</template>

<style lang="scss" scoped>
.icon-action {
  opacity: 0.4;
  transition: opacity linear 0.15s;
}

.icon-action:hover {
  opacity: 1;
}
</style>
